import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { authApi } from '@/services/api';
import { Logo } from '@/components/shared/Logo';
import { ROUTES } from '@/routes';
import { Shield, Database, Zap } from 'lucide-react';

export function LoginPage() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [isRedirecting, setIsRedirecting] = useState(false);

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to={ROUTES.app.dashboard} replace />;

  const error = searchParams.get('error');

  const handleGoogleLogin = async () => {
    setIsRedirecting(true);
    try {
      const response = await authApi.getAuthUrl('google');
      window.location.href = response.data.url;
    } catch {
      setIsRedirecting(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="flex flex-1 flex-col items-center justify-center px-8">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="mx-auto mb-4 flex justify-center">
              <Logo size="lg" linkTo="/" />
            </div>
            <p className="mt-2 text-muted-foreground">
              Smart travel expense tracking with automatic trip detection
            </p>
          </div>

          {error && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error === 'not_allowed'
                ? 'This Google account is not allowed to sign in to this Tally instance.'
                : 'Sign-in failed. Please try again.'}
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            disabled={isRedirecting}
            className="flex w-full items-center justify-center gap-3 rounded-xl border border-border bg-card px-6 py-3.5 text-sm font-medium shadow-sm transition-all hover:bg-accent hover:shadow-md disabled:opacity-50"
          >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            Continue with Google
          </button>

          <p className="text-center text-xs text-muted-foreground">
            Signing in also grants read-only Gmail access for receipt scanning. You can connect additional Gmail accounts later.
          </p>
        </div>
      </div>

      <div className="hidden flex-1 items-center justify-center bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 lg:flex">
        <div className="max-w-md space-y-8 p-12">
          <h2 className="text-2xl font-semibold">Your finances, organized automatically</h2>
          <div className="space-y-6">
            <Feature icon={Zap} title="Auto-detect trips" description="Flights, hotels, and activities grouped into trips automatically from your email" />
            <Feature icon={Database} title="Self-hosted" description="Your financial data stays on your own cloud instance. Nobody else has access." />
            <Feature icon={Shield} title="Bank-level security" description="AES-256 encryption for all stored credentials. OAuth tokens never exposed client-side." />
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, description }: { icon: any; title: string; description: string }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div>
        <h3 className="font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
